import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useGetAllTimeLogsByDateQuery,
  useBulkProcessDayLogsMutation,
} from 'redux/features/timeLogApi';
import { useGetUsersQuery } from 'redux/features/users/userApi';
import { useGetSettingsQuery } from 'redux/features/settings/settingsApi';
import useUser from 'hooks/useUser';
import Loader from 'components/ui/Loader';
import Icon from 'components/AppIcon';
import dayjs from 'lib/dayjs';
import { toast } from 'react-toastify';
import DayLogEditModal from 'pages/dashboard-overview/components/DayLogEditModal';

const calculateOTMinutes = (workedMinutes, regularMinutes) => {
  const slab = 30; // 30 min blocks
  if (workedMinutes <= regularMinutes) return 0;
  const otMinutes = Math.round(workedMinutes) - regularMinutes;
  return Math.floor(otMinutes / slab) * slab;
};

const AttendanceTab = () => {
  const currentUser = useUser();
  const navigate = useNavigate();
  const { data: settingsResponse } = useGetSettingsQuery();
  const systemSettings = settingsResponse?.data;

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());

  const sliderRef = useRef(null);

  useEffect(() => {
    if (sliderRef.current) {
      const activeEl = sliderRef.current.querySelector('[data-active="true"]');
      if (activeEl) {
        activeEl.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [selectedDay, selectedMonth, selectedYear]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedUserRow, setExpandedUserRow] = useState(null);

  // Editing logs
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingUserLogs, setEditingUserLogs] = useState([]);
  const [editingDate, setEditingDate] = useState(null);

  const [bulkProcessDayLogs] = useBulkProcessDayLogsMutation();

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const currentDay = new Date().getDate();

  // Number of days in the selected month
  const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();

  // Correct days array depending on if it is the current month
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1).filter((d) => {
    if (selectedYear === currentYear && selectedMonth === currentMonth) {
      return d <= currentDay;
    }
    return true;
  });

  useEffect(() => {
    // If selectedDay becomes invalid when month/year changes
    const maxDay = new Date(selectedYear, selectedMonth, 0).getDate();
    if (selectedDay > maxDay) {
      setSelectedDay(maxDay);
    } else if (
      selectedYear === currentYear &&
      selectedMonth === currentMonth &&
      selectedDay > currentDay
    ) {
      setSelectedDay(currentDay);
    }
  }, [selectedMonth, selectedYear, currentDay, currentMonth, currentYear, selectedDay]);

  const handlePreviousMonth = () => {
    let newMonth = selectedMonth - 1;
    let newYear = selectedYear;

    if (newMonth < 1) {
      newMonth = 12;
      newYear = selectedYear - 1;
    }

    if (newYear >= 2010) {
      setSelectedMonth(newMonth);
      setSelectedYear(newYear);
      setSelectedDay(1);
    }
  };

  const handleNextMonth = () => {
    let newMonth = selectedMonth + 1;
    let newYear = selectedYear;

    if (newMonth > 12) {
      newMonth = 1;
      newYear = selectedYear + 1;
    }

    if (newYear < currentYear || (newYear === currentYear && newMonth <= currentMonth)) {
      setSelectedMonth(newMonth);
      setSelectedYear(newYear);
      setSelectedDay(1);
    }
  };

  const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;

  // Fetch users list
  const { data: usersResponse, isLoading: isUsersLoading } = useGetUsersQuery();
  const users = usersResponse?.data?.users || [];

  // Fetch logs for the selected day
  const {
    data: { timeLogs = [], leaves = [], holiday = null } = {},
    isFetching: isLogsFetching,
    refetch: refetchLogs,
  } = useGetAllTimeLogsByDateQuery(
    {
      date: dateStr,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  // Filter users based on search
  const filteredUsers = users.filter((u) => {
    if (u.role === 'SUPER_ADMIN') return false; // Hide super admin
    const fullName = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
    const employeeId = (u.employee_id || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    return fullName.includes(query) || employeeId.includes(query);
  });

  const toggleUserRow = (userId) => {
    if (expandedUserRow === userId) {
      setExpandedUserRow(null);
    } else {
      setExpandedUserRow(userId);
    }
  };

  const handleEditLogs = (user, userLogs) => {
    setEditingUserId(user.id);
    setEditingUserLogs(userLogs);
    setEditingDate(dayjs(dateStr).toDate());
    setEditModalOpen(true);
  };

  const handleBulkDayUpdate = async (payload) => {
    try {
      await bulkProcessDayLogs({ userId: editingUserId, ...payload }).unwrap();
      toast.success(
        currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPER_ADMIN'
          ? 'Logs updated successfully'
          : 'Log update requests submitted successfully',
      );
      setEditModalOpen(false);
      refetchLogs();
    } catch (error) {
      console.error(error?.data?.message || 'Failed to update logs');
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '--:--';
    return dayjs(timeStr).format('hh:mm A');
  };

  const formatHoursMinutes = (decimalHours) => {
    if (!decimalHours) return '--';
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    if (hours === 0) return `${minutes}m`;
    return `${hours}h ${minutes}m`;
  };

  const getDayName = (dayNumber) => {
    const d = new Date(selectedYear, selectedMonth - 1, dayNumber);
    return d.toLocaleDateString('en-US', { weekday: 'short' });
  };

  return (
    <div className="space-y-6">
      {/* Date Navigation & Calendar Strip */}
      <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
              <Icon name="CalendarDays" size={20} className="text-primary-500" />
              <span>Daily Attendance Logs</span>
            </h2>
            <p className="text-xs text-gray-500 font-medium">
              Select any date to view and manage attendance details.
            </p>
          </div>

          {/* Month/Year selectors */}
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePreviousMonth}
              disabled={selectedYear === 2010 && selectedMonth === 1}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>

            <div className="flex items-center space-x-2">
              <div className="relative">
                <select
                  value={selectedMonth}
                  onChange={(e) => {
                    setSelectedMonth(parseInt(e.target.value));
                    setSelectedDay(1);
                  }}
                  className="appearance-none px-4 py-2 pr-8 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white text-gray-700 font-semibold text-sm min-w-[120px]"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1)
                    .filter((m) => (selectedYear === currentYear ? m <= currentMonth : true))
                    .map((m) => (
                      <option key={m} value={m}>
                        {dayjs()
                          .month(m - 1)
                          .format('MMMM')}
                      </option>
                    ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"></div>
              </div>

              <div className="relative">
                <select
                  value={selectedYear}
                  onChange={(e) => {
                    setSelectedYear(parseInt(e.target.value));
                    setSelectedDay(1);
                  }}
                  className="appearance-none px-4 py-2 pr-8 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white text-gray-700 font-semibold text-sm min-w-[90px]"
                >
                  {Array.from({ length: currentYear - 2010 + 1 }, (_, i) => currentYear - i).map(
                    (y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ),
                  )}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"></div>
              </div>
            </div>

            <button
              onClick={handleNextMonth}
              disabled={selectedYear === currentYear && selectedMonth === currentMonth}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </div>
        </div>

        {/* Horizontal Day Slider */}
        <div ref={sliderRef} className="flex gap-2 pb-2 flex-wrap justify-start">
          {days.map((day) => {
            const isSelected = selectedDay === day;
            const isTodayDay =
              selectedYear === currentYear && selectedMonth === currentMonth && day === currentDay;
            return (
              <button
                key={day}
                data-active={isSelected}
                onClick={() => setSelectedDay(day)}
                className={`flex flex-col items-center justify-center min-w-[40px] h-[45px] rounded-xl border transition-all duration-150 ${
                  isSelected
                    ? 'bg-primary-600 border-primary-600 text-white shadow-md shadow-primary-100 font-semibold'
                    : isTodayDay
                      ? 'bg-primary-50 border-primary-200 text-primary-700 font-bold'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="text-[10px] uppercase tracking-wider opacity-85">
                  {getDayName(day)}
                </span>
                <span className="text-lg font-bold mt-0.5">{day}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Search and Table */}
      <div className="space-y-4">
        {/* Search */}
        <div className="relative max-w-md bg-white rounded-xl shadow-sm border border-gray-100">
          <input
            type="text"
            placeholder="Search employee by name or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-transparent rounded-xl focus:ring-2 focus:ring-primary-500 transition-all duration-200 text-sm"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon name="Search" size={16} className="text-gray-400" />
          </div>
        </div>

        {/* Table Container */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          {isUsersLoading || isLogsFetching ? (
            <div className="py-20 flex justify-center">
              <Loader />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white">
              <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4 border border-gray-100">
                <Icon name="Users" size={28} className="text-gray-300" />
              </div>
              <p className="text-sm font-bold text-gray-900 uppercase tracking-widest">
                No Employees Found
              </p>
            </div>
          ) : (
            <div className="overflow-auto min-h-[300px] max-h-[60vhm] scrollbar-thin">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/70 border-b border-gray-200">
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-500">
                      Employee
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-500">
                      Check In
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-500">
                      Check Out
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-500 text-center">
                      Working Hours
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-500 text-center">
                      Total Hours
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-500 text-center">
                      OT
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-500 text-center">
                      Leave / Event
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-500 text-center">
                      Status
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-500 text-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredUsers.map((user) => {
                    const userLogs = timeLogs
                      .filter((log) => log.user_id === user.id)
                      .sort(
                        (a, b) => new Date(a.check_in).getTime() - new Date(b.check_in).getTime(),
                      );
                    const rowOpen = expandedUserRow === user.id;
                    const userLeave = leaves.find((l) => l.user_id === user.id);
                    const dayName = getDayName(selectedDay);

                    // Compute basic checkin/checkout metrics
                    const firstCheckIn =
                      userLogs.length > 0
                        ? Math.min(
                            ...userLogs
                              .filter((l) => l.check_in)
                              .map((l) => new Date(l.check_in).getTime()),
                          )
                        : null;
                    const lastCheckOut =
                      userLogs.length > 0 && userLogs.every((l) => l.check_out)
                        ? Math.max(
                            ...userLogs
                              .filter((l) => l.check_out)
                              .map((l) => new Date(l.check_out).getTime()),
                          )
                        : null;

                    const workingHours = userLogs.reduce(
                      (sum, log) => sum + (log.total_hours || 0),
                      0,
                    );
                    const breakHours = systemSettings?.break_hours || 0;
                    const totalHours = workingHours > 0 ? workingHours + breakHours : 0;
                    const isOvertimeEnabled = user.designation?.overtime_enabled ?? false;
                    const regularMinutes = systemSettings?.standard_day_minutes || 480;
                    const overtimeMinutes =
                      isOvertimeEnabled && totalHours > 0
                        ? calculateOTMinutes(totalHours * 60, regularMinutes)
                        : 0;
                    const overtimeHours = overtimeMinutes / 60;

                    const hasActiveSession = userLogs.some((l) => !l.check_out);

                    let statusLabel = 'Not Checked In';
                    let statusClass = 'bg-gray-100 text-gray-700 border-gray-200';

                    if (userLogs.length > 0) {
                      if (hasActiveSession) {
                        statusLabel = 'Checked In';
                        statusClass = 'bg-green-50 text-green-700 border-green-200 animate-pulse';
                      } else {
                        statusLabel = 'Checked Out';
                        statusClass = 'bg-blue-50 text-blue-700 border-blue-200';
                      }
                    }

                    return (
                      <React.Fragment key={user.id}>
                        {/* Main row */}
                        <tr
                          onClick={() => toggleUserRow(user.id)}
                          className={`cursor-pointer transition-colors duration-150 ${
                            rowOpen ? 'bg-primary-50/20' : 'hover:bg-gray-50/50'
                          }`}
                        >
                          {/* Profile */}
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <button
                                type="button"
                                className={`flex justify-center items-center w-7 h-7 rounded-full border transition-all duration-150 ${
                                  rowOpen
                                    ? 'bg-white border-primary-200 text-primary-600 shadow-sm'
                                    : 'bg-gray-50 border-gray-200 text-gray-400'
                                }`}
                              >
                                <Icon size={14} name={rowOpen ? 'ChevronUp' : 'ChevronDown'} />
                              </button>
                              <div className="w-9 h-9 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-bold text-sm">
                                {user.first_name?.charAt(0)}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-900">
                                  {user.first_name} {user.last_name}
                                </p>
                                <p className="text-xs text-gray-500">ID: {user.employee_id}</p>
                              </div>
                            </div>
                          </td>

                          {/* First Check In */}
                          <td className="px-6 py-4 text-sm font-medium text-gray-600">
                            {firstCheckIn ? formatTime(new Date(firstCheckIn)) : '--:--'}
                          </td>

                          {/* Last Check Out */}
                          <td className="px-6 py-4 text-sm font-medium text-gray-600">
                            {hasActiveSession ? (
                              <span className="text-green-600 font-bold">Active</span>
                            ) : lastCheckOut ? (
                              formatTime(new Date(lastCheckOut))
                            ) : (
                              '--:--'
                            )}
                          </td>

                          {/* Working Hours */}
                          <td className="px-6 py-4 text-center">
                            <span className="inline-flex w-[80px] justify-center items-center px-2 py-1 rounded-md bg-gray-50 text-gray-500 text-[13px] font-bold border border-gray-100 tracking-tight">
                              {workingHours > 0 ? formatHoursMinutes(workingHours) : '--'}
                            </span>
                          </td>

                          {/* Total Hours */}
                          <td className="px-6 py-4 text-center">
                            {totalHours > 0 ? (
                              <span
                                className={`text-xs font-bold px-2.5 py-1 rounded-lg inline-flex justify-center min-w-[70px] ${
                                  totalHours >= 9
                                    ? 'text-emerald-700 bg-emerald-50 border border-emerald-100'
                                    : totalHours >= 7
                                      ? 'text-amber-700 bg-amber-50 border border-amber-100'
                                      : 'text-rose-700 bg-rose-50 border border-rose-100'
                                }`}
                              >
                                {formatHoursMinutes(totalHours)}
                              </span>
                            ) : (
                              <span className="text-[10px] font-bold text-gray-400 px-2.5 py-1 inline-flex justify-center min-w-[70px] bg-gray-50 border border-gray-100 rounded-lg">
                                --
                              </span>
                            )}
                          </td>

                          {/* Extra Hours */}
                          <td className="px-6 py-4 text-center">
                            {overtimeHours > 0 ? (
                              <span className="text-xs font-bold px-2.5 py-1 rounded-lg text-indigo-700 bg-indigo-50 border border-indigo-100 min-w-[70px] inline-flex justify-center">
                                {formatHoursMinutes(overtimeHours)}
                              </span>
                            ) : (
                              <span className="text-[10px] font-bold text-gray-400 px-2.5 py-1 inline-flex justify-center min-w-[70px] bg-gray-50 border border-gray-100 rounded-lg">
                                --
                              </span>
                            )}
                          </td>

                          {/* Leave / Event */}
                          <td className="px-6 py-4 text-center">
                            {userLeave ? (
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                                  userLeave.status === 'APPROVED'
                                    ? 'bg-purple-50 text-purple-700 border-purple-200'
                                    : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                }`}
                              >
                                {userLeave.status === 'APPROVED' ? 'Approved' : 'Pending'}{' '}
                                {userLeave.type
                                  ? userLeave.type.replace(/_/g, ' ').toLowerCase()
                                  : 'Leave'}
                              </span>
                            ) : holiday ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border bg-pink-50 text-pink-700 border-pink-200">
                                {holiday.title || 'Holiday'}
                              </span>
                            ) : dayName === 'Sun' ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border bg-gray-50 text-gray-500 border-gray-200">
                                Sunday Off
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs font-medium">--</span>
                            )}
                          </td>

                          {/* Status Badge */}
                          <td className="px-6 py-4 text-center">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${statusClass}`}
                            >
                              {statusLabel}
                            </span>
                          </td>

                          {/* Actions */}
                          <td className="px-6 py-4 text-center">
                            <div
                              className="flex items-center justify-center space-x-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() => handleEditLogs(user, userLogs)}
                                className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-xs rounded-lg transition-colors duration-150"
                              >
                                <Icon name="Edit3" size={14} />
                                <span>Edit Logs</span>
                              </button>
                              <button
                                onClick={() =>
                                  navigate(
                                    `/employee-management/employee-time-logs?id=${user.id}`,
                                    {
                                      state: { navFrom: '/attendance-salary' },
                                    },
                                  )
                                }
                                className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-primary-50 hover:bg-primary-100 text-primary-700 font-semibold text-xs rounded-lg transition-colors duration-150"
                              >
                                <Icon name="Calendar" size={14} />
                                <span>View Month</span>
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded details row */}
                        {rowOpen && (
                          <tr>
                            <td
                              colSpan="9"
                              className="bg-gray-50/50 px-8 py-5 border-t border-gray-100"
                            >
                              <div className="space-y-3 max-w-4xl">
                                <div className="flex items-center justify-between">
                                  <p className="font-bold text-xs uppercase tracking-[0.15em] text-gray-400">
                                    Log Session Details
                                  </p>
                                </div>

                                {userLogs.length === 0 ? (
                                  <div className="flex flex-col items-center justify-center py-6 bg-white/50 rounded-xl border border-dashed border-gray-200">
                                    <Icon name="Ghost" size={20} className="text-gray-300 mb-1" />
                                    <p className="text-xs font-medium text-gray-400 uppercase tracking-widest">
                                      No active sessions recorded for this day
                                    </p>
                                  </div>
                                ) : (
                                  <div className="grid gap-3">
                                    {userLogs.map((detail, index) => {
                                      let breakBetween = '';
                                      const nextLog = userLogs[index + 1];
                                      if (nextLog) {
                                        const breakDuration = dayjs(nextLog.check_in).diff(
                                          dayjs(detail.check_out),
                                          'minutes',
                                        );
                                        breakBetween = formatHoursMinutes(breakDuration / 60);
                                      }

                                      return (
                                        <div
                                          key={detail.id || index}
                                          className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm"
                                        >
                                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                                            <div>
                                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
                                                Check In
                                              </p>
                                              <p className="text-sm font-bold text-gray-700">
                                                {detail.check_in
                                                  ? formatTime(detail.check_in)
                                                  : '--:--'}
                                              </p>
                                            </div>

                                            <div>
                                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
                                                Check Out
                                              </p>
                                              <p className="text-sm font-bold text-gray-700">
                                                {detail.check_out
                                                  ? formatTime(detail.check_out)
                                                  : '--:--'}
                                              </p>
                                            </div>

                                            <div>
                                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
                                                Duration
                                              </p>
                                              <p className="text-sm font-bold text-emerald-600">
                                                {formatHoursMinutes(detail.total_hours || 0)}
                                              </p>
                                            </div>

                                            <div>
                                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
                                                Reason
                                              </p>
                                              <p
                                                className="text-xs font-medium text-gray-600 truncate"
                                                title={detail.reason}
                                              >
                                                {detail.reason || 'Regular Session'}
                                                {breakBetween && (
                                                  <span className="text-amber-500 ml-1 font-bold">
                                                    ({breakBetween} Break)
                                                  </span>
                                                )}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit Log Modal */}
      {editModalOpen && (
        <DayLogEditModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setEditingUserId(null);
            setEditingUserLogs([]);
            setEditingDate(null);
          }}
          date={editingDate}
          logs={editingUserLogs}
          onSubmit={handleBulkDayUpdate}
          user={currentUser}
          targetUserId={editingUserId}
        />
      )}
    </div>
  );
};

export default AttendanceTab;
